/**
 * Dialog — the app's own confirm / prompt / alert, replacing the browser's
 * window.confirm / window.prompt / window.alert system popups.
 *
 * Imperative, promise-based API so a call site is a near 1:1 swap:
 *
 *   if (!(await confirmDialog('Delete this?'))) return
 *   const note = await promptDialog({ title: 'Reject?', placeholder: 'Optional note', multiline: true })
 *   if (note === null) return            // cancelled
 *   await alertDialog('Saved.')
 *
 *   // A native date picker instead of typed YYYY-MM-DD text — pass inputType: 'date'.
 *   const d = await promptDialog({ title: 'Edit date', defaultValue: '2026-09-17', inputType: 'date' })
 *
 * Resolution contract:
 *   confirmDialog     -> boolean        (Cancel/Escape => false)
 *   promptDialog      -> string | null  (Cancel/Escape => null; otherwise the entered text, possibly '')
 *   alertDialog       -> undefined      (always resolves on OK/Escape)
 *   confirmTypeDialog -> boolean        (Cancel/Escape => false; typing the exact confirmWord and
 *                                        clicking Delete => true). Use for cascading/irreversible deletes.
 *   choiceDialog      -> {value, note} | null  (Cancel/Escape/Skip => null; otherwise the selected
 *                                        choice's value + the optional note, possibly ''). Use for a
 *                                        single-select "which of these" prompt (e.g. the phase
 *                                        closeout categorization) — a picker, not free text.
 *
 * <DialogHost/> is mounted ONCE (in OutlineView). Calls made before it mounts queue and
 * render when it does. Multiple concurrent calls stack (first-in renders first).
 */

import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

/* ── imperative bridge ───────────────────────────────────────────────────── */

let _counter = 0
let _queue = []
const _listeners = new Set()
function _emit() { _listeners.forEach(l => l(_queue)) }

function _open(spec) {
  return new Promise(resolve => {
    _queue = [..._queue, { ..._normalize(spec), _id: ++_counter, _resolve: resolve }]
    _emit()
  })
}
function _resolveTop(value) {
  const top = _queue[0]
  if (!top) return
  _queue = _queue.slice(1)
  _emit()
  top._resolve(value)
}
function _normalize(opts) {
  return typeof opts === 'string' ? { message: opts } : (opts || {})
}

export function confirmDialog(opts) { return _open({ kind: 'confirm', ..._normalize(opts) }) }
export function promptDialog(opts)  { return _open({ kind: 'prompt',  ..._normalize(opts) }) }
export function alertDialog(opts)   { return _open({ kind: 'alert',   ..._normalize(opts) }) }

/**
 * A destructive confirm that requires the user to type an exact word (default
 * "DELETE") before the danger button enables. Use for cascading/irreversible
 * deletes where a plain confirmDialog isn't enough friction.
 *   const ok = await confirmTypeDialog({ title: 'Delete unit?', message: '...' })
 * Resolution: boolean (Cancel/Escape => false; typed word must match exactly).
 */
export function confirmTypeDialog(opts) { return _open({ kind: 'confirmType', danger: true, confirmWord: 'DELETE', ..._normalize(opts) }) }

/**
 * A single-select "which of these" picker, optionally with a free-text note.
 *   const r = await choiceDialog({
 *     title: 'Why did this phase run over?',
 *     choices: [{ value: 'scope', label: 'Scope crept beyond the contract' }, ...],
 *     allowNote: true, notePlaceholder: 'Optional context',
 *   })
 *   if (!r) return   // skipped/cancelled
 *   // r.value, r.note
 * Resolution: { value, note } | null (Cancel/Escape/Skip => null).
 */
export function choiceDialog(opts) { return _open({ kind: 'choice', ..._normalize(opts) }) }

/* ── view ────────────────────────────────────────────────────────────────── */

const FONT = '"DM Sans",sans-serif'
// Theme via the app's existing CSS custom properties (outline.css :root / body.dark) rather than
// useDarkMode() — these already flip automatically, so no re-render wiring is needed here.
const INK = 'var(--aj-text)'
// Primary/accent button fill stays the constant brand dark-teal in both themes (matches every
// other primary button in the app, e.g. AdminDashboard's "Open editor") — never theme-swapped,
// since white-on-INK would lose contrast once INK goes light in dark mode.
const BRAND = '#07272D'
const DANGER = '#c0392b'

// Stable, readable debug id for a dialog (shown by the /debug modal badge so a specific dialog can be
// named precisely). Prefer an explicit dlg.dialogId; else derive from the title; else the kind.
function slug(s) { return String(s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') }
function dialogModalId(dlg) { return dlg.dialogId || ('dialog:' + (slug(dlg.title) || dlg.kind || 'dialog')) }

function btnStyle({ primary, danger, ghost }) {
  const base = { padding: '7px 16px', fontFamily: FONT, fontSize: 12.5, fontWeight: 600, borderRadius: 4, cursor: 'pointer', lineHeight: 1.2 }
  if (ghost) return { ...base, fontWeight: 500, background: 'none', border: '0.5px solid var(--aj-border-mid)', color: INK }
  if (danger) return { ...base, background: DANGER, border: 'none', color: '#fff' }
  if (primary) return { ...base, background: BRAND, border: 'none', color: '#fff' }
  return base
}

function DialogView({ dlg }) {
  const { kind, title, message, danger } = dlg
  const isPrompt = kind === 'prompt'
  const isAlert = kind === 'alert'
  const isConfirmType = kind === 'confirmType'
  const isChoice = kind === 'choice'
  const confirmWord = dlg.confirmWord || 'DELETE'
  const [value, setValue] = useState(isPrompt ? (dlg.defaultValue || '') : '')
  const [choiceValue, setChoiceValue] = useState(null)
  const [noteValue, setNoteValue] = useState('')
  const inputRef = useRef(null)

  useEffect(() => {
    const t = setTimeout(() => {
      if (inputRef.current) { inputRef.current.focus(); inputRef.current.select?.() }
    }, 20)
    return () => clearTimeout(t)
  }, [])

  // Auto-size the multiline prompt textarea to its content (so it opens fitting the existing text and
  // grows as you type), capped at ~half the viewport, then it scrolls.
  useEffect(() => {
    if (!isPrompt || !dlg.multiline) return
    const el = inputRef.current
    if (!el || el.tagName !== 'TEXTAREA') return
    el.style.height = 'auto'
    const cap = Math.round(window.innerHeight * 0.5)
    el.style.height = Math.min(cap, Math.max(64, el.scrollHeight)) + 'px'
  }, [value, isPrompt, dlg.multiline])

  function confirm() {
    if (isPrompt) {
      if (dlg.required && !value.trim()) return
      _resolveTop(value)
    } else if (isConfirmType) {
      if (value.trim() !== confirmWord) return
      _resolveTop(true)
    } else if (isAlert) {
      _resolveTop(undefined)
    } else if (isChoice) {
      if (!choiceValue) return
      _resolveTop({ value: choiceValue, note: noteValue })
    } else {
      _resolveTop(true)
    }
  }
  function cancel() { _resolveTop(isPrompt ? null : isConfirmType ? false : isAlert ? undefined : isChoice ? null : false) }

  function onKeyDown(e) {
    if (e.key === 'Escape') { e.preventDefault(); cancel(); return }
    if (e.key === 'Enter') {
      // In a multiline prompt (or the choice dialog's note textarea), plain Enter inserts a
      // newline; Cmd/Ctrl+Enter confirms.
      if (((isPrompt && dlg.multiline) || (isChoice && e.target.tagName === 'TEXTAREA')) && !(e.metaKey || e.ctrlKey)) return
      e.preventDefault(); confirm()
    }
  }

  const confirmLabel = dlg.confirmLabel || (isAlert ? (dlg.okLabel || 'OK') : isPrompt ? 'Save' : isConfirmType ? 'Delete' : isChoice ? 'Save' : 'Confirm')
  const cancelLabel = dlg.cancelLabel || (isChoice ? (dlg.skipLabel || 'Skip') : 'Cancel')
  const confirmDisabled = (isPrompt && dlg.required && !value.trim()) || (isConfirmType && value.trim() !== confirmWord) || (isChoice && !choiceValue)

  const inputBase = {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontFamily: FONT, fontSize: 13,
    color: INK, background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)', borderRadius: 4, outline: 'none',
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1000100 /* above every gate scrim (TimeGate/ProgressGate/TriageGate sit at 1000000, the gate's peek at 1000001) — a confirm opened from a gate used to land BEHIND it (Matt, 2026-09-10) */, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onKeyDown={onKeyDown}
    >
      <div
        role="dialog"
        aria-modal="true"
        data-modal-id={dialogModalId(dlg)}
        style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.30)', width: 420, maxWidth: 'calc(100vw - 32px)', padding: '20px 20px 16px' }}
        onMouseDown={e => e.stopPropagation()}
      >
        {title && <div style={{ fontFamily: FONT, fontSize: 14, fontWeight: 600, color: INK, marginBottom: message ? 8 : 14 }}>{title}</div>}
        {message && <div style={{ fontFamily: FONT, fontSize: 12.5, lineHeight: 1.5, color: 'var(--aj-text-mid)', marginBottom: isPrompt ? 12 : 16, whiteSpace: 'pre-wrap' }}>{message}</div>}

        {isPrompt && (dlg.multiline
          ? <textarea ref={inputRef} value={value} onChange={e => setValue(e.target.value)} placeholder={dlg.placeholder || ''} style={{ ...inputBase, resize: 'none', overflowY: 'auto', minHeight: 64, maxHeight: '50vh', marginBottom: 16, fontFamily: FONT }} />
          : <input ref={inputRef} type={dlg.inputType || 'text'} value={value} onChange={e => setValue(e.target.value)} placeholder={dlg.placeholder || ''} style={{ ...inputBase, marginBottom: 16 }} />
        )}

        {isConfirmType && (
          <input
            ref={inputRef}
            value={value}
            onChange={e => setValue(e.target.value)}
            placeholder={`Type ${confirmWord} to confirm`}
            style={{ ...inputBase, marginBottom: 16, fontFamily: '"DM Mono",monospace', letterSpacing: '0.04em' }}
          />
        )}

        {isChoice && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: dlg.allowNote ? 10 : 16 }}>
            {(dlg.choices || []).map(c => {
              const selected = choiceValue === c.value
              return (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setChoiceValue(c.value)}
                  style={{
                    textAlign: 'left', padding: '9px 12px', borderRadius: 6, cursor: 'pointer',
                    fontFamily: FONT, fontSize: 12.5, fontWeight: selected ? 600 : 400, color: INK,
                    background: selected ? 'var(--aj-bg-subtle)' : 'none',
                    border: selected ? `1.5px solid ${BRAND}` : '0.5px solid var(--aj-border)',
                  }}
                >
                  {c.label}
                  {c.description && <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--aj-text-mid)', marginTop: 2 }}>{c.description}</div>}
                </button>
              )
            })}
          </div>
        )}
        {isChoice && dlg.allowNote && (
          <textarea
            value={noteValue}
            onChange={e => setNoteValue(e.target.value)}
            placeholder={dlg.notePlaceholder || 'Optional note'}
            style={{ ...inputBase, resize: 'none', minHeight: 50, marginBottom: 16, fontFamily: FONT }}
          />
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          {!isAlert && <button onClick={cancel} style={btnStyle({ ghost: true })}>{cancelLabel}</button>}
          <button ref={(!isPrompt && !isConfirmType) ? inputRef : null} onClick={confirm} disabled={confirmDisabled}
            style={{ ...btnStyle({ primary: !danger, danger }), opacity: confirmDisabled ? 0.5 : 1, cursor: confirmDisabled ? 'not-allowed' : 'pointer' }}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

export function DialogHost() {
  const [queue, setQueue] = useState(_queue)
  useEffect(() => {
    const l = q => setQueue(q)
    _listeners.add(l)
    setQueue(_queue)
    return () => { _listeners.delete(l) }
  }, [])

  useEffect(() => {
    if (queue.length) { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = '' } }
  }, [queue.length])

  const dlg = queue[0]
  if (!dlg) return null
  return createPortal(<DialogView key={dlg._id} dlg={dlg} />, document.body)
}
